import { useCallback, useEffect, useMemo, useState } from "react";
import { listarEmpleados, obtenerEmpleado } from "../../../../services/empleadosService";
import { normalizeEmpleado } from "../adapters/empleadosAdapter";
import { showToast } from "../../../../utils/showToast";

const initialFilters = { search: "", tipo_personal: "all", tipo_contratacion: "all", estado: "activo" };

export default function useEmpleadosList() {
  const [empleados, setEmpleados] = useState([]);
  const [seleccionado, setSeleccionado] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [filtros, setFiltros] = useState(initialFilters);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchEmpleados = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarEmpleados();
      setEmpleados((Array.isArray(data) ? data : []).map(normalizeEmpleado));
      setError(null);
      setLastUpdated(new Date());
    } catch (err) {
      const message = err.message || "No se pudo obtener los empleados.";
      setError(message);
      showToast({ type: "danger", message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchEmpleados(); }, [fetchEmpleados]);

  const resumen = useMemo(() => {
    const total           = empleados.length;
    const activos         = empleados.filter((e) => e.estado === "activo").length;
    const inactivos       = total - activos;
    const docentes        = empleados.filter((e) => e.tipoPersonal === "docente").length;
    const administrativos = empleados.filter((e) => e.tipoPersonal === "administrativo").length;
    const servicios       = empleados.filter((e) => e.tipoPersonal === "servicios").length;
    const enNomina        = empleados.filter((e) => e.tipoContratacion === "nomina").length;
    const porServicios    = empleados.filter((e) => e.tipoContratacion === "servicios").length;
    return { total, activos, inactivos, docentes, administrativos, servicios, enNomina, porServicios };
  }, [empleados]);

  const empleadosFiltrados = useMemo(() => {
    return empleados.filter((emp) => {
      if (filtros.tipo_personal !== "all" && emp.tipoPersonal !== filtros.tipo_personal) return false;
      if (filtros.tipo_contratacion !== "all" && emp.tipoContratacion !== filtros.tipo_contratacion) return false;
      if (filtros.estado !== "all" && emp.estado !== filtros.estado) return false;
      if (filtros.search) {
        const term = filtros.search.toLowerCase();
        const haystack = `${emp.nombres} ${emp.apellidos} ${emp.cedula} ${emp.email} ${emp.puestoNombre}`.toLowerCase();
        if (!haystack.includes(term)) return false;
      }
      return true;
    });
  }, [empleados, filtros]);

  const total      = empleadosFiltrados.length;
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  useEffect(() => { setPage(1); }, [filtros, total]);

  const empleadosPaginados = useMemo(() => {
    const start = (page - 1) * pageSize;
    return empleadosFiltrados.slice(start, start + pageSize);
  }, [empleadosFiltrados, page, pageSize]);

  const goToPage = useCallback(
    (next) => setPage(Math.min(totalPages, Math.max(1, next))),
    [totalPages]
  );

  const upsertEmpleado = useCallback((emp) => {
    if (!emp?.id) return;
    setEmpleados((prev) => {
      const exists = prev.some((item) => String(item.id) === String(emp.id));
      if (exists) return prev.map((item) => String(item.id) === String(emp.id) ? normalizeEmpleado({ ...item, ...emp }) : item);
      return [normalizeEmpleado(emp), ...prev];
    });
    setLastUpdated(new Date());
  }, []);

  const removeEmpleado = useCallback((id) => {
    setEmpleados((prev) => prev.filter((item) => String(item.id) !== String(id)));
    setLastUpdated(new Date());
  }, []);

  const seleccionar = useCallback(async (emp) => {
    if (!emp) { setSeleccionado(null); return; }
    if (typeof emp === "object") setSeleccionado(normalizeEmpleado(emp));
    try {
      const detalle = await obtenerEmpleado(emp.id ?? emp);
      setSeleccionado(normalizeEmpleado(detalle));
    } catch (err) {
      showToast({ type: "danger", message: "No se pudo cargar el empleado." });
    }
  }, []);

  const limpiarFiltros = useCallback(() => setFiltros(initialFilters), []);

  return {
    empleados,
    empleadosFiltrados,
    empleadosPaginados,
    seleccionado,
    setSeleccionado,
    loading,
    error,
    resumen,
    filtros,
    setFiltros,
    limpiarFiltros,
    lastUpdated,
    page,
    pageSize,
    setPageSize,
    totalPages,
    total,
    goToPage,
    reload: fetchEmpleados,
    seleccionar,
    upsertEmpleado,
    removeEmpleado,
  };
}

export { initialFilters as initialEmpleadoFilters };
